import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import DiodeScreen from "@/components/DiodeScreen";
import BootSequence from "@/components/BootSequence";
import HoldScreen from "@/components/HoldScreen";
import HardResetDialog from "@/components/HardResetDialog";
import IntroErrorBoundary from "@/components/IntroErrorBoundary";
import StandbyScreen from "@/components/StandbyScreen";
import ControlButton from "@/components/ControlButton";
import ResetDialog from "@/components/ResetDialog";
import useViewportHeight from "@/hooks/useViewportHeight";
import { clearSessionId } from "@/lib/darknessApi";
import { initSfx, stopAllSfx, prefetchSfx } from "@/lib/sfx";
import { enterKiosk, keepAwake } from "@/lib/device";
import { checkForUpdate, reloadForUpdate } from "@/registerSW";
import { serverReachable, wipeAndReload } from "@/lib/hardReset";

const UPDATE_CHECK_MS = 60000;
const HOLD_KEY = "spore_stage";

function Stage({ stage, go }) {
  if (stage === "loading") return <div data-testid="app-loading" />;
  if (stage === "standby") return <StandbyScreen />;
  if (stage === "hold") return <HoldScreen />;
  return <IntroErrorBoundary onError={go.toHold}><Intro stage={stage} go={go} /></IntroErrorBoundary>;
}

function Intro({ stage, go }) {
  if (stage === "diode1")
    return <DiodeScreen key="d1" number="ONE" onTap={go.unlock} onConfirmed={go.toDiodeTwo} />;
  if (stage === "diode2")
    return <DiodeScreen key="d2" number="TWO" previous="ONE" onTap={go.unlock} onConfirmed={go.toBoot} />;
  return <BootSequence onDone={go.toHold} />;
}

function App() {
  useViewportHeight();
  const [stage, setStage] = useState("loading");
  const [confirming, setConfirming] = useState(false);
  const [hardReset, setHardReset] = useState(null);

  useEffect(() => {
    prefetchSfx();
    clearSessionId();
    setStage(localStorage.getItem(HOLD_KEY) === "hold" ? "hold" : "standby");
  }, []);

  const start = () => {
    initSfx();
    enterKiosk();
    keepAwake(true);
    setStage("diode1");
  };

  const go = {
    unlock: initSfx,
    toDiodeTwo: useCallback(() => setStage("diode2"), []),
    toBoot: useCallback(() => setStage("boot"), []),
    toHold: useCallback(() => {
      localStorage.setItem(HOLD_KEY, "hold");
      setStage("hold");
    }, []),
  };

  const teardown = () => {
    stopAllSfx(0.1);
    keepAwake(false);
    localStorage.removeItem(HOLD_KEY);
    setStage("loading");
  };

  const reset = () => {
    setConfirming(false);
    teardown();
    window.location.reload();
  };

  const confirmHardReset = async () => {
    setHardReset("checking");
    if (!(await serverReachable())) return setHardReset("offline");
    setHardReset(null);
    teardown();
    await wipeAndReload();
  };

  useEffect(() => {
    if (stage !== "standby") return;
    const tick = () => checkForUpdate().then((u) => { if (u) reloadForUpdate(); });
    tick();
    const t = setInterval(tick, UPDATE_CHECK_MS);
    return () => clearInterval(t);
  }, [stage]);

  return (
    <div className="spore-root" data-testid="spore-app" data-stage={stage}>
      <div className="stage-wrap">
        <Stage stage={stage} go={go} />
      </div>
      <div className="impact-flash" aria-hidden="true" />
      <div className="spore-scanlines" aria-hidden="true" />
      <div className="spore-vignette" aria-hidden="true" />
      <button type="button" className="control-btn hard-reset-btn" data-testid="hard-reset-button" onClick={() => setHardReset("confirm")}>
        HARD RESET
      </button>
      {stage !== "loading" && (
        <ControlButton mode={stage === "standby" ? "start" : "reset"} onStart={start} onReset={() => setConfirming(true)} />
      )}
      {confirming && <ResetDialog onCancel={() => setConfirming(false)} onConfirm={reset} />}
      <div className="rotate-overlay" data-testid="rotate-overlay">
        <p className="glow">ROTATE TABLET TO LANDSCAPE</p>
      </div>
      {hardReset && <HardResetDialog state={hardReset} onCancel={() => setHardReset(null)} onConfirm={confirmHardReset} />}
    </div>
  );
}

export default App;
